import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useRef, useState } from "react";
import type { Settings } from "@instead/shared";
import { api } from "../lib/api.ts";
import { wipeDevice } from "../lib/device.ts";
import { applyAppearance } from "../lib/theme.ts";
import { useT } from "../lib/i18n.ts";
import { Ic } from "../components/Icons.tsx";
import { Info, Toast, useToast } from "../components/Ui.tsx";

/* Settings sub-pages (round 5: the hub was one long scroll — each group is
   now its own screen; round 6 folded Rest into Shelf, D-34). Every row is
   still one line; saves are optimistic. */

export function Select<T extends string>(props: { id: string; label: string; value: T; options: [T, string][]; onChange: (v: T) => void }) {
  return (
    <div className="settings-row">
      <label htmlFor={props.id}>{props.label}</label>
      <select id={props.id} className="input" style={{ minWidth: 150, minHeight: 36 }} value={props.value} onChange={(e) => props.onChange(e.target.value as T)}>
        {props.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}

export function Toggle(props: { id: string; label: string; checked: boolean; onChange: (v: boolean) => void; info?: string }) {
  const t = useT();
  return (
    <div className="settings-row" style={props.info ? { flexWrap: "wrap" } : undefined}>
      <label htmlFor={props.id} style={{ flex: "0 1 auto" }}>{props.label}</label>
      {props.info && <Info text={props.info} label={`${t("settings.about")} ${props.label}`} />}
      <span style={{ flex: 1 }} />
      <input id={props.id} type="checkbox" role="switch" style={{ width: 24, height: 24, accentColor: "var(--accent)" }} checked={props.checked} onChange={(e) => props.onChange(e.target.checked)} />
    </div>
  );
}

/**
 * The settings document + an optimistic `set` (instant appearance, §7.6).
 * Quick successive changes (theme, then accent, then language) each build on
 * the cache, and only the newest write's answer lands back in it — an older
 * response, or a refetch, must never roll a later change back.
 */
export function useSettingsEditor() {
  const qc = useQueryClient();
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: api.settings.get });
  const seq = useRef(0);
  const save = useMutation({
    mutationFn: (v: { next: Settings; n: number }) => api.settings.put(v.next),
    onMutate: async ({ next }) => {
      await qc.cancelQueries({ queryKey: ["settings"] });
      applyAppearance(next);
      qc.setQueryData(["settings"], next);
    },
    onSuccess: (saved, { n }) => { if (n === seq.current) qc.setQueryData(["settings"], saved); },
  });
  const set = (patch: (draft: Settings) => void) => {
    const base = qc.getQueryData<Settings>(["settings"]) ?? settings;
    if (!base) return;
    const next = structuredClone(base);
    patch(next);
    if (next.rest.defaultCards > next.rest.maxCards) next.rest.defaultCards = next.rest.maxCards;
    seq.current += 1;
    save.mutate({ next, n: seq.current });
  };
  return { settings, set };
}

function SubHead({ title }: { title: string }) {
  const t = useT();
  const nav = useNavigate();
  return (
    <div className="screen-head">
      <button className="icon-btn" aria-label={t("back")} onClick={() => nav({ to: "/settings" })}>{Ic.back()}</button>
      <h1 className="title">{title}</h1>
    </div>
  );
}

export function AppearanceSettings() {
  const t = useT();
  const { settings: s, set } = useSettingsEditor();
  if (!s) return null;
  return (
    <div>
      <SubHead title={t("settings.appearance")} />
      <Select id="set-theme" label={t("settings.theme")} value={s.appearance.theme}
        options={[["system", t("settings.theme.system")], ["light", t("settings.theme.light")], ["dark", t("settings.theme.dark")]]}
        onChange={(v) => set((d) => { d.appearance.theme = v; })} />
      <Select id="set-pack" label={t("settings.pack")} value={s.appearance.pack}
        options={[["sage", "Sage"], ["clay", "Clay"], ["slate", "Slate"], ["plum", "Plum"], ["mono", "Mono"]]}
        onChange={(v) => set((d) => { d.appearance.pack = v; })} />
      <Select id="set-textsize" label={t("settings.textSize")} value={s.appearance.textSize}
        options={[["system", t("settings.theme.system")], ["s", "S"], ["m", "M"], ["l", "L"], ["xl", "XL"]]}
        onChange={(v) => set((d) => { d.appearance.textSize = v; })} />
      <Select id="set-density" label={t("settings.density")} value={s.appearance.density}
        options={[["comfortable", t("settings.density.comfortable")], ["compact", t("settings.density.compact")]]}
        onChange={(v) => set((d) => { d.appearance.density = v; })} />
      <Select id="set-browsing" label={t("settings.browsing")} value={s.appearance.browsing}
        options={[["continuous", t("settings.browsing.continuous")], ["paged", t("settings.browsing.paged")]]}
        onChange={(v) => set((d) => { d.appearance.browsing = v; })} />
      <Select id="set-locale" label={t("settings.language")} value={s.locale}
        options={[["system", t("settings.theme.system")], ["en", "English"], ["it", "Italiano"]]}
        onChange={(v) => set((d) => { d.locale = v; })} />
      <Toggle id="set-contrast" label={t("settings.highContrast")} checked={s.appearance.highContrast}
        onChange={(v) => set((d) => { d.appearance.highContrast = v; })} />
      <Toggle id="set-feedback-btn" label={t("settings.feedbackButton")} checked={s.features.feedbackButton ?? true} info={t("settings.feedbackButtonInfo")}
        onChange={(v) => set((d) => { d.features.feedbackButton = v; })} />
    </div>
  );
}

/**
 * Shelf settings (round 6, D-34): everything that decides what can be dealt
 * to you lives on one page — how many cards, the taxonomy, the needs and
 * places features (each with its own page), and the sources. The former
 * Rest page is folded in here; `/settings/rest` redirects.
 */
export function ShelfSettings() {
  const t = useT();
  const { settings: s, set } = useSettingsEditor();
  if (!s) return null;
  const nums = (from: number, to: number): [string, string][] => Array.from({ length: to - from + 1 }, (_, i) => [String(from + i), String(from + i)]);
  const row = (to: string, label: string) => (
    <Link className="nav-row" to={to}>
      <span className="grow">{label}</span>
      <span style={{ color: "var(--ink-3)", display: "inline-flex" }}>{Ic.chevron({ size: 16 })}</span>
    </Link>
  );
  return (
    <div>
      <SubHead title={t("settings.shelf")} />
      <Select id="set-default-cards" label={t("settings.defaultCards")} value={String(s.rest.defaultCards)}
        options={nums(1, s.rest.maxCards)} onChange={(v) => set((d) => { d.rest.defaultCards = parseInt(v, 10); })} />
      <Select id="set-max-cards" label={t("settings.maxCards")} value={String(s.rest.maxCards)}
        options={nums(1, 5)} onChange={(v) => set((d) => { d.rest.maxCards = parseInt(v, 10); })} />
      {row("/settings/categories", t("settings.categories"))}
      {row("/settings/moods", t("settings.moods"))}
      <Toggle id="set-f-needs" label={t("settings.features.needs")} checked={s.features.needs} info={t("settings.features.needsInfo")}
        onChange={(v) => set((d) => { d.features.needs = v; })} />
      {s.features.needs && row("/settings/needs", t("needs.title"))}
      <Toggle id="set-f-context" label={t("settings.features.context")} checked={s.features.context} info={t("settings.features.contextInfo")}
        onChange={(v) => set((d) => { d.features.context = v; })} />
      {s.features.context && row("/settings/places", t("settings.places"))}
      {row("/sources", t("settings.sources"))}
    </div>
  );
}

export function AccountSettings() {
  const t = useT();
  const nav = useNavigate();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [toast, say] = useToast();
  return (
    <div>
      <SubHead title={t("settings.account")} />
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--sp-2)" }}>
        <button className="btn btn-block" onClick={async () => {
          try { await api.account.export(); say(t("account.exported")); } catch (e) { say(e instanceof Error ? e.message : "Export failed."); }
        }}>{t("account.export")}</button>
        <button className="btn btn-block" onClick={async () => { await wipeDevice(); await nav({ to: "/signin" }); }}>
          {t("settings.signout")}
        </button>
        {!deleteOpen ? (
          <button className="btn btn-danger btn-block" onClick={() => setDeleteOpen(true)}>{t("account.delete")}</button>
        ) : (
          <form onSubmit={async (e) => {
            e.preventDefault();
            if (deleteConfirm !== "DELETE") return;
            try { await api.account.remove(); } catch (err) { say(err instanceof Error ? err.message : "Delete failed."); return; }
            await wipeDevice(); // the server side is gone; now the device (D-33)
            await nav({ to: "/signin" });
          }}>
            <div role="alert" className="meta" style={{ color: "var(--danger)", marginBottom: "var(--sp-2)" }}>
              <p style={{ margin: 0 }}>{t("account.deleteWarning")}</p>
              <p style={{ margin: "var(--sp-2) 0 0" }}>{t("account.deleteNote")}</p>
            </div>
            <div className="field">
              <label htmlFor="del-confirm">{t("account.deleteConfirmLabel")}</label>
              <input id="del-confirm" className="input" autoComplete="off" value={deleteConfirm} onChange={(e) => setDeleteConfirm(e.target.value)} />
            </div>
            <div style={{ display: "flex", gap: "var(--sp-2)" }}>
              <button className="btn" type="button" onClick={() => { setDeleteOpen(false); setDeleteConfirm(""); }}>{t("cancel")}</button>
              <button className="btn btn-danger" type="submit" disabled={deleteConfirm !== "DELETE"}>{t("account.deleteForever")}</button>
            </div>
          </form>
        )}
      </div>
      <div style={{ marginTop: "var(--sp-5)" }}>
        <Link className="nav-row" to="/privacy">
          <span className="grow">{t("legal.privacy")}</span>
          <span style={{ color: "var(--ink-3)", display: "inline-flex" }}>{Ic.chevron({ size: 16 })}</span>
        </Link>
        <Link className="nav-row" to="/terms">
          <span className="grow">{t("legal.terms")}</span>
          <span style={{ color: "var(--ink-3)", display: "inline-flex" }}>{Ic.chevron({ size: 16 })}</span>
        </Link>
      </div>
      <Toast msg={toast} />
    </div>
  );
}
